import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { NavItem } from '../../types';

interface SubmenuPanelProps {
  items: NavItem[];
  activeGroupId: string | null;
}

export default function SubmenuPanel({ items, activeGroupId }: SubmenuPanelProps) {
  const t = (k: string) => k.split('.').pop() ?? k;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const activeGroup = items.find((item) => item.id === activeGroupId);
  const groupLabel = activeGroup ? t(activeGroup.labelKey) : '';
  const children = activeGroup?.children ?? [];

  const q = query.toLowerCase();
  const filteredChildren = children.filter(
    (c) => !q || t(c.labelKey).toLowerCase().includes(q) || c.label.toLowerCase().includes(q)
  );

  return (
    <div className="flex h-full w-60 flex-col border-r border-border bg-surface-sidebar">
      {/* Search toggle + section label */}
      <div className="border-b border-border px-4 py-2">
        {searchOpen ? (
          <div className="flex w-full items-center gap-2 rounded-md border border-primary bg-surface-card px-2 py-1.5">
            <Search size={14} className="shrink-0 text-content-muted" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchOpen(false);
                  setQuery('');
                }
              }}
              className="min-w-0 flex-1 bg-transparent text-xs text-content-primary outline-none placeholder:text-content-muted"
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={() => {
                setSearchOpen(false);
                setQuery('');
              }}
              className="shrink-0 text-content-muted hover:text-content-primary"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="flex-1 truncate text-[11px] font-semibold uppercase tracking-wider text-content-muted">
              {groupLabel}
            </span>
            <button
              type="button"
              aria-label="Open search"
              onClick={() => setSearchOpen(true)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
            >
              <Search size={14} />
            </button>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-1">
          {children.length === 0 && (
            <li className="px-3 py-2 text-xs text-content-muted">
              No submenu for this section
            </li>
          )}
          {children.length > 0 && filteredChildren.length === 0 && (
            <li className="px-3 py-2 text-xs text-content-muted">No matches</li>
          )}
          {filteredChildren.map((child) => {
            const isActive = location.pathname === child.path;
            return (
              <li key={child.id}>
                <button
                  onClick={() => navigate(child.path)}
                  className={cn(
                    'flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-primary-light text-primary font-medium'
                      : 'text-content-secondary hover:bg-surface-card-hover hover:text-content-primary'
                  )}
                >
                  {t(child.labelKey)}
                  {child.badge ? (
                    <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                      {child.badge}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
